// Consolidated Finance module pages: Orders, Invoices, Revenue, Reports, Expenses,
// and "coming soon" pages for Inbox, Contracts, Tax, Forecast, plus Settings.

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Plus, Trash2, Edit2, PackageCheck, FileText, TrendingUp, BarChart3,
  Receipt, Inbox, FileSignature, Landmark, Sparkles, Download, CheckCircle2,
  Mail, DollarSign, Settings as SettingsIcon, X, Camera, Image as ImageIcon, Upload,
  Paperclip, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import { FinanceLayout, Pill, Empty, ComingSoon, money } from './FinanceLayout';
import { useAuth } from '../../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FastAPI returns 422 detail as an array of {msg, loc, ...} — stringify so Sonner doesn't crash.
const errMsg = (err, fallback = 'Failed') => {
  const d = err?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : x?.msg || JSON.stringify(x))).join(', ') || fallback;
  if (typeof d === 'object' && d.message) return d.message;
  return d?.msg || JSON.stringify(d);
};

// Returns the parsed dedup error if backend signalled one, else null.
//   { kind: 'duplicate_receipt' | 'possible_duplicate', message, existing }
const parseDedupError = (err) => {
  if (err?.response?.status !== 409) return null;
  const d = err?.response?.data?.detail;
  if (!d || typeof d !== 'object') return null;
  if (d.code !== 'duplicate_receipt' && d.code !== 'possible_duplicate') return null;
  return { kind: d.code, message: d.message || '', existing: d.existing || null };
};

// ====================== ORDERS ======================

export function FinanceOrdersPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ customer_name: '', customer_email: '', country: '', shipping_usd: 0, tax_usd: 0, fees_usd: 0, discount_usd: 0, status: 'paid', line_items: [{ product_id: '', product_name: '', variant: '', quantity: 1, unit_price_usd: 0, unit_cost_usd: 0 }] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [o, p] = await Promise.all([
        axios.get(`${API}/admin/finance/orders`, { headers }),
        axios.get(`${API}/admin/finance/products`, { headers }),
      ]);
      setOrders(o.data);
      setProducts(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/finance/orders`, form, { headers });
      toast.success('Order recorded');
      setShowForm(false);
      setForm({ customer_name: '', customer_email: '', country: '', shipping_usd: 0, tax_usd: 0, fees_usd: 0, discount_usd: 0, status: 'paid', line_items: [{ product_id: '', product_name: '', variant: '', quantity: 1, unit_price_usd: 0, unit_cost_usd: 0 }] });
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
    finally { setBusy(false); }
  };

  const setItem = (idx, patch) => {
    const next = [...form.line_items];
    next[idx] = { ...next[idx], ...patch };
    // auto-fill when product chosen
    if (patch.product_id) {
      const p = products.find(x => x.product_id === patch.product_id);
      if (p) {
        next[idx].product_name = p.name;
        next[idx].unit_price_usd = p.retail_price_usd;
        next[idx].unit_cost_usd = p.cost_per_unit_usd;
      }
    }
    setForm({ ...form, line_items: next });
  };

  const actions = (
    <button onClick={() => setShowForm(!showForm)} data-testid="toggle-order-form" className="bg-white text-blue-700 px-4 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2 text-sm">
      <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'New order'}
    </button>
  );

  return (
    <FinanceLayout title="Merch orders" subtitle={`${orders.length} total`} actions={actions}>
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="order-form">
          <h3 className="font-black text-slate-900">New order</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <L label="Customer name" req><I value={form.customer_name} onChange={v => setForm({ ...form, customer_name: v })} testid="ord-name" /></L>
            <L label="Email"><I type="email" value={form.customer_email} onChange={v => setForm({ ...form, customer_email: v })} testid="ord-email" /></L>
            <L label="Country"><I value={form.country} onChange={v => setForm({ ...form, country: v })} testid="ord-country" /></L>
          </div>
          <div className="space-y-2">
            <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Items</div>
            {form.line_items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <select value={it.product_id} onChange={e => setItem(idx, { product_id: e.target.value })} className="col-span-4 border border-slate-300 rounded-lg px-2 py-1.5 bg-white text-sm">
                  <option value="">— select product —</option>
                  {products.map(p => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
                </select>
                <input placeholder="Variant" value={it.variant} onChange={e => setItem(idx, { variant: e.target.value })} className="col-span-2 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" placeholder="Qty" value={it.quantity} onChange={e => setItem(idx, { quantity: Number(e.target.value) })} className="col-span-1 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" step="0.01" placeholder="Price" value={it.unit_price_usd} onChange={e => setItem(idx, { unit_price_usd: Number(e.target.value) })} className="col-span-2 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" step="0.01" placeholder="Cost" value={it.unit_cost_usd} onChange={e => setItem(idx, { unit_cost_usd: Number(e.target.value) })} className="col-span-2 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <button type="button" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })} className="col-span-1 text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, line_items: [...form.line_items, { product_id: '', product_name: '', variant: '', quantity: 1, unit_price_usd: 0, unit_cost_usd: 0 }] })} className="text-sm font-bold text-blue-600 hover:text-blue-800">+ Add item</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <L label="Shipping $"><I type="number" value={form.shipping_usd} onChange={v => setForm({ ...form, shipping_usd: Number(v) })} /></L>
            <L label="Discount $"><I type="number" value={form.discount_usd} onChange={v => setForm({ ...form, discount_usd: Number(v) })} /></L>
            <L label="Tax $"><I type="number" value={form.tax_usd} onChange={v => setForm({ ...form, tax_usd: Number(v) })} /></L>
            <L label="Processing fees $"><I type="number" value={form.fees_usd} onChange={v => setForm({ ...form, fees_usd: Number(v) })} /></L>
          </div>
          <div className="flex justify-end gap-2">
            <button type="submit" disabled={busy} data-testid="ord-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">
              {busy ? 'Saving…' : 'Record order'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" /></div>
      ) : orders.length === 0 ? (
        <Empty text="No merch orders yet. Once Printful or Shopify is connected these will flow in automatically. For now you can record orders manually above." />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <Th>Order</Th><Th>Customer</Th><Th>Country</Th><Th align="right">Gross</Th>
                <Th align="right">COGS</Th><Th align="right">Net</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.map(o => (
                <tr key={o.order_id} className="hover:bg-slate-50" data-testid={`ord-row-${o.order_id}`}>
                  <Td bold>{o.order_number}</Td>
                  <Td>{o.customer_name}<div className="text-xs text-slate-500">{o.customer_email}</div></Td>
                  <Td>{o.country || '—'}</Td>
                  <Td align="right">{money(o.gross_usd)}</Td>
                  <Td align="right" className="text-slate-500">-{money(o.cogs_usd)}</Td>
                  <Td align="right" bold>{money(o.net_usd)}</Td>
                  <Td><Pill tone={o.status === 'paid' || o.status === 'fulfilled' ? 'emerald' : o.status === 'refunded' || o.status === 'canceled' ? 'rose' : 'amber'}>{o.status}</Pill></Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </FinanceLayout>
  );
}

// ====================== INVOICES ======================

export function FinanceInvoicesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_type: 'advertiser', client_name: '', client_email: '', client_address: '', client_tax_id: '', issued_date: new Date().toISOString().slice(0, 10), due_date: '', tax_rate_percent: 0, notes: '', line_items: [{ description: '', quantity: 1, unit_price_usd: 0 }] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/finance/invoices`, { headers });
      setInvoices(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/admin/finance/invoices`, form, { headers });
      toast.success('Invoice created (draft)');
      setShowForm(false);
      setForm({ client_type: 'advertiser', client_name: '', client_email: '', client_address: '', client_tax_id: '', issued_date: new Date().toISOString().slice(0, 10), due_date: '', tax_rate_percent: 0, notes: '', line_items: [{ description: '', quantity: 1, unit_price_usd: 0 }] });
      load();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
    finally { setBusy(false); }
  };

  const markStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/finance/invoices/${id}`, { status }, { headers });
      toast.success(`Marked ${status}`); load();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this invoice? (only drafts can be deleted)')) return;
    try { await axios.delete(`${API}/admin/finance/invoices/${id}`, { headers }); toast.success('Deleted'); load(); }
    catch (err) { toast.error(errMsg(err, 'Failed')); }
  };

  const subtotal = form.line_items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price_usd) || 0), 0);
  const tax = subtotal * (Number(form.tax_rate_percent) || 0) / 100;
  const total = subtotal + tax;

  const actions = (
    <button onClick={() => setShowForm(!showForm)} data-testid="toggle-invoice-form" className="bg-white text-blue-700 px-4 py-2 rounded-full font-bold shadow-lg hover:scale-105 transition flex items-center gap-2 text-sm">
      <Plus className="w-4 h-4" /> {showForm ? 'Close' : 'New invoice'}
    </button>
  );

  return (
    <FinanceLayout title="Invoices" subtitle={`${invoices.length} total`} actions={actions}>
      {showForm && (
        <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" data-testid="invoice-form">
          <h3 className="font-black text-slate-900">New invoice</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <L label="Client type">
              <select value={form.client_type} onChange={e => setForm({ ...form, client_type: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="inv-type">
                <option value="advertiser">Advertiser</option>
                <option value="analytics">Analytics client</option>
                <option value="merch">Merch buyer</option>
                <option value="sponsor">Sponsor</option>
                <option value="other">Other</option>
              </select>
            </L>
            <L label="Client name" req><I value={form.client_name} onChange={v => setForm({ ...form, client_name: v })} testid="inv-name" /></L>
            <L label="Email"><I type="email" value={form.client_email} onChange={v => setForm({ ...form, client_email: v })} testid="inv-email" /></L>
            <L label="Issued date"><I type="date" value={form.issued_date} onChange={v => setForm({ ...form, issued_date: v })} /></L>
            <L label="Due date"><I type="date" value={form.due_date} onChange={v => setForm({ ...form, due_date: v })} /></L>
            <L label="Tax rate %"><I type="number" value={form.tax_rate_percent} onChange={v => setForm({ ...form, tax_rate_percent: v })} /></L>
            <L label="Billing address" spans={2}><I value={form.client_address} onChange={v => setForm({ ...form, client_address: v })} /></L>
            <L label="Tax ID / VAT"><I value={form.client_tax_id} onChange={v => setForm({ ...form, client_tax_id: v })} /></L>
          </div>
          <div className="space-y-2">
            <div className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Line items</div>
            {form.line_items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                <input placeholder="Description" value={it.description} onChange={e => { const n = [...form.line_items]; n[idx].description = e.target.value; setForm({ ...form, line_items: n }); }} className="col-span-6 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" placeholder="Qty" value={it.quantity} onChange={e => { const n = [...form.line_items]; n[idx].quantity = Number(e.target.value); setForm({ ...form, line_items: n }); }} className="col-span-2 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <input type="number" step="0.01" placeholder="Unit price" value={it.unit_price_usd} onChange={e => { const n = [...form.line_items]; n[idx].unit_price_usd = Number(e.target.value); setForm({ ...form, line_items: n }); }} className="col-span-3 border border-slate-300 rounded-lg px-2 py-1.5 text-sm" />
                <button type="button" onClick={() => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) })} className="col-span-1 text-slate-400 hover:text-rose-500"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, line_items: [...form.line_items, { description: '', quantity: 1, unit_price_usd: 0 }] })} className="text-sm font-bold text-blue-600 hover:text-blue-800">+ Add line</button>
          </div>
          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between"><span>Subtotal</span><span>{money(subtotal)}</span></div>
            <div className="flex justify-between text-slate-600"><span>Tax ({form.tax_rate_percent}%)</span><span>{money(tax)}</span></div>
            <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1 mt-1"><span>Total</span><span>{money(total)}</span></div>
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={busy} data-testid="inv-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">
              {busy ? 'Saving…' : 'Create draft'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" /></div>
      ) : invoices.length === 0 ? (
        <Empty text="No invoices yet. Create your first invoice for an advertiser, analytics client, or sponsor." />
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <Th>Invoice</Th><Th>Client</Th><Th>Issued</Th><Th>Due</Th>
                <Th align="right">Total</Th><Th>Status</Th><Th></Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {invoices.map(i => (
                <tr key={i.invoice_id} className="hover:bg-slate-50" data-testid={`inv-row-${i.invoice_id}`}>
                  <Td bold>{i.invoice_number}</Td>
                  <Td>{i.client_name}<div className="text-xs text-slate-500">{i.client_email}</div></Td>
                  <Td>{i.issued_date}</Td>
                  <Td>{i.due_date || '—'}</Td>
                  <Td align="right" bold>{money(i.total_usd)}</Td>
                  <Td><Pill tone={i.status === 'paid' ? 'emerald' : i.status === 'sent' ? 'blue' : i.status === 'void' ? 'rose' : 'slate'}>{i.status}</Pill></Td>
                  <Td align="right">
                    {i.status === 'draft' && <button onClick={() => markStatus(i.invoice_id, 'sent')} data-testid={`send-inv-${i.invoice_id}`} className="text-xs font-bold text-blue-600 mr-2">Mark sent</button>}
                    {i.status === 'sent' && <button onClick={() => markStatus(i.invoice_id, 'paid')} data-testid={`paid-inv-${i.invoice_id}`} className="text-xs font-bold text-emerald-600 mr-2">Mark paid</button>}
                    {i.status === 'draft' && <button onClick={() => del(i.invoice_id)} data-testid={`del-inv-${i.invoice_id}`} className="text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>}
                    {i.status !== 'draft' && i.status !== 'void' && <button onClick={() => markStatus(i.invoice_id, 'void')} className="text-xs font-bold text-rose-500">Void</button>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </FinanceLayout>
  );
}

// ====================== REVENUE ======================

export function FinanceRevenuePage() {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  useEffect(() => {
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/accounting/summary?months=12`, { headers }),
      axios.get(`${API}/admin/finance/revenue-breakdown`, { headers }),
    ]).then(([s, b]) => { setSummary(s.data); setBreakdown(b.data); });
  }, [token]);

  if (!summary || !breakdown) return <FinanceLayout title="Revenue"><div className="py-20 flex justify-center"><div className="animate-spin rounded-full h-10 w-10 border-4 border-teal-500 border-t-transparent" /></div></FinanceLayout>;

  return (
    <FinanceLayout title="Revenue" subtitle="All channels">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        <RevenueCard icon={TrendingUp} label="Analytics subscriptions" main={money(breakdown.subscription_mrr)} sub={`${breakdown.active_subscriptions} active · ${money(breakdown.subscription_arr)} ARR`} tone="blue" />
        <RevenueCard icon={TrendingUp} label="Advertising (live)" main={money(breakdown.ad_active_value_usd)} sub={`${breakdown.ad_active_campaigns} active campaigns`} tone="violet" />
        <RevenueCard icon={Receipt} label="Outstanding" main={money(breakdown.outstanding_invoices_total_usd)} sub={`${breakdown.outstanding_invoices_count} unpaid invoices`} tone="amber" />
        <RevenueCard icon={TrendingUp} label="Merchandise lifetime" main={money(breakdown.merch_lifetime_revenue_usd)} sub={`${breakdown.merch_paid_order_count} paid orders · ${money(breakdown.merch_lifetime_net_usd)} net`} tone="orange" />
      </div>
      <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <h2 className="text-lg font-black text-slate-900 p-5 pb-3">Monthly revenue — 12 month trend</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-y border-slate-200">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <Th>Month</Th><Th align="right">Subscriptions</Th><Th align="right">Tours</Th><Th align="right">Other</Th><Th align="right">Total</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {summary.series.slice().reverse().map(m => (
                <tr key={m.month}>
                  <Td>{m.label}</Td>
                  <Td align="right">{money(m.subscription_revenue)}</Td>
                  <Td align="right">{money(m.tour_revenue)}</Td>
                  <Td align="right">{money(m.other_revenue)}</Td>
                  <Td align="right" bold>{money(m.revenue_total)}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </FinanceLayout>
  );
}

const RevenueCard = ({ icon: Icon, label, main, sub, tone }) => {
  const accent = { blue: 'from-blue-500 to-indigo-600', violet: 'from-violet-500 to-fuchsia-600', amber: 'from-amber-500 to-orange-500', orange: 'from-orange-500 to-rose-500' }[tone];
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-gradient-to-r ${accent} text-white text-[10px] font-bold uppercase tracking-wider mb-2`}>
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-2xl font-black text-slate-900">{main}</div>
      <div className="text-[11px] text-slate-500 mt-1">{sub}</div>
    </div>
  );
};

// ====================== REPORTS ======================

export function FinanceReportsPage() {
  const { token } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());

  const download = async () => {
    try {
      const res = await axios.get(`${API}/admin/accounting/export.csv?year=${year}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `audiostride-${year}-tax.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  return (
    <FinanceLayout title="Reports" subtitle="Tax-ready exports and P&L">
      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <h2 className="font-black text-slate-900 flex items-center gap-2"><Download className="w-5 h-5" /> Tax-ready CSV export</h2>
        <p className="text-sm text-slate-600">One row per transaction and expense for the selected year — fully categorized and ready to hand to your accountant.</p>
        <div className="flex items-end gap-3">
          <L label="Year"><I type="number" value={year} onChange={v => setYear(Number(v) || year)} testid="report-year" /></L>
          <button onClick={download} data-testid="download-csv" className="px-5 py-2.5 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm flex items-center gap-2">
            <Download className="w-4 h-4" /> Download {year} CSV
          </button>
        </div>
      </div>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700">
        <p className="font-bold text-blue-900">Monthly P&amp;L</p>
        <p className="mt-1">For the full 12-month breakdown, head to the <strong>Dashboard</strong> — the last table is a complete monthly P&amp;L with subscription revenue, tour revenue, fees, expenses, and net income.</p>
      </div>
    </FinanceLayout>
  );
}

// ====================== EXPENSES (delegated to existing accounting page) ======================
// We keep /admin/accounting live too — this page links to the same underlying data.

export function FinanceExpensesPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [expenses, setExpenses] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), vendor: '', description: '', amount_usd: '', category: 'Software', recurring: false, notes: '' });
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [receiptB64, setReceiptB64] = useState('');
  const [receiptName, setReceiptName] = useState('');
  const [receiptPreview, setReceiptPreview] = useState('');
  const [viewingReceipt, setViewingReceipt] = useState(null); // {expense_id, image_base64, mime_type, filename, vendor, amount_usd, date}
  const [filterReceipt, setFilterReceipt] = useState('all'); // all | with | without
  const receiptInputRef = React.useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Honour ?filter=without|with|all when arriving via deep link from the Dashboard.
  useEffect(() => {
    const f = searchParams.get('filter');
    if (f === 'without' || f === 'with' || f === 'all') {
      setFilterReceipt(f);
    }
    // eslint-disable-next-line
  }, []);

  // Keep URL in sync when user clicks a pill (so the link is shareable)
  const setFilter = (id) => {
    setFilterReceipt(id);
    if (id === 'all') {
      searchParams.delete('filter');
    } else {
      searchParams.set('filter', id);
    }
    setSearchParams(searchParams, { replace: true });
  };

  const load = async () => {
    try {
      const [e, s] = await Promise.all([
        axios.get(`${API}/admin/accounting/expenses`, { headers }),
        axios.get(`${API}/admin/accounting/settings`, { headers }),
      ]);
      setExpenses(e.data); setSettings(s.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onReceiptFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setReceiptPreview(reader.result);
      setReceiptB64(reader.result.split(',')[1] || reader.result);
      setReceiptName(file.name || `receipt-${Date.now()}.jpg`);
    };
    reader.readAsDataURL(file);
  };

  const clearReceipt = () => { setReceiptB64(''); setReceiptName(''); setReceiptPreview(''); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...form, amount_usd: Number(form.amount_usd) };
      if (receiptB64) {
        body.receipt_image_base64 = receiptB64;
        body.receipt_filename = receiptName || `receipt-${Date.now()}.jpg`;
        body.receipt_mime_type = receiptPreview.startsWith('data:') ? receiptPreview.split(';')[0].slice(5) : 'image/jpeg';
      }
      const submit = (force) => editingId
        ? axios.put(`${API}/admin/accounting/expenses/${editingId}`, body, { headers })
        : axios.post(`${API}/admin/accounting/expenses${force ? '?force=true' : ''}`, body, { headers });
      try {
        await submit(false);
      } catch (err) {
        const dedup = parseDedupError(err);
        if (dedup?.kind === 'duplicate_receipt') {
          toast.error(dedup.message || 'This exact receipt is already filed.');
          setBusy(false); return;
        }
        if (dedup?.kind === 'possible_duplicate') {
          if (!window.confirm(`${dedup.message}\n\nFile this expense anyway?`)) {
            setBusy(false); return;
          }
          await submit(true);
        } else {
          throw err;
        }
      }
      toast.success(editingId ? 'Updated' : `Expense added${receiptB64 ? ' with receipt' : ''}`);
      setForm({ date: new Date().toISOString().slice(0, 10), vendor: '', description: '', amount_usd: '', category: settings?.categories?.[0] || 'Software', recurring: false, notes: '' });
      setEditingId(null); clearReceipt(); load();
    } catch (err) { toast.error(errMsg(err, 'Failed')); }
    finally { setBusy(false); }
  };

  const edit = (ex) => {
    setForm({ ...ex, date: (ex.date || '').slice(0, 10) });
    setEditingId(ex.expense_id);
    clearReceipt();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const del = async (id) => {
    if (!window.confirm('Delete this expense? Any attached receipt will also be removed.')) return;
    try { await axios.delete(`${API}/admin/accounting/expenses/${id}`, { headers }); toast.success('Deleted'); load(); }
    catch { toast.error('Failed'); }
  };

  const viewReceipt = async (ex) => {
    try {
      const { data } = await axios.get(`${API}/admin/accounting/expenses/${ex.expense_id}/receipt`, { headers });
      setViewingReceipt({ ...data, expense_id: ex.expense_id, vendor: ex.vendor, amount_usd: ex.amount_usd, date: ex.date, description: ex.description });
    } catch (err) {
      toast.error(errMsg(err, 'Could not load receipt'));
    }
  };

  const removeReceipt = async (expenseId) => {
    if (!window.confirm('Remove the attached receipt? The expense itself will stay.')) return;
    try {
      await axios.delete(`${API}/admin/accounting/expenses/${expenseId}/receipt`, { headers });
      toast.success('Receipt removed');
      setViewingReceipt(null);
      load();
    } catch { toast.error('Failed'); }
  };

  const attachLater = (expenseId) => {
    // Inline upload for an expense that has no receipt yet
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,application/pdf';
    input.onchange = async (ev) => {
      const file = ev.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await axios.post(`${API}/admin/accounting/expenses/${expenseId}/receipt`, {
            receipt_image_base64: reader.result.split(',')[1] || reader.result,
            receipt_filename: file.name,
            receipt_mime_type: file.type || 'image/jpeg',
          }, { headers });
          toast.success('Receipt attached');
          load();
        } catch (err) {
          const dedup = parseDedupError(err);
          if (dedup?.kind === 'duplicate_receipt') {
            toast.error(dedup.message || 'This exact receipt is already filed on another expense.');
          } else {
            toast.error(errMsg(err, 'Upload failed'));
          }
        }
      };
      reader.readAsDataURL(file);
    };
    input.click();
  };

  const filtered = expenses.filter(e => {
    if (filterReceipt === 'with') return !!e.has_receipt;
    if (filterReceipt === 'without') return !e.has_receipt;
    return true;
  });
  const withReceiptCount = expenses.filter(e => e.has_receipt).length;

  return (
    <FinanceLayout title="Expenses" subtitle={`${expenses.length} records · ${withReceiptCount} with receipts`}>
      <form onSubmit={save} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3" data-testid="expense-form">
        <h3 className="font-black text-slate-900">{editingId ? 'Edit expense' : 'Add expense'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <L label="Date" req><I type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} testid="exp-date" /></L>
          <L label="Vendor"><I value={form.vendor} onChange={v => setForm({ ...form, vendor: v })} testid="exp-vendor" /></L>
          <L label="Amount (USD)" req><I type="number" value={form.amount_usd} onChange={v => setForm({ ...form, amount_usd: v })} testid="exp-amount" /></L>
          <L label="Category" req>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="exp-category">
              {(settings?.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </L>
          <L label="Description" req spans={2}><I value={form.description} onChange={v => setForm({ ...form, description: v })} testid="exp-desc" /></L>
          <L label="Notes" spans={3}><I value={form.notes} onChange={v => setForm({ ...form, notes: v })} testid="exp-notes" /></L>
        </div>

        {/* Receipt uploader */}
        <div className="border border-dashed border-slate-300 rounded-xl p-3 bg-slate-50/40">
          <input
            ref={receiptInputRef}
            type="file"
            accept="image/*,application/pdf"
            className="hidden"
            onChange={(ev) => { onReceiptFile(ev.target.files?.[0]); ev.target.value = ''; }}
            data-testid="exp-receipt-file"
          />
          {receiptPreview ? (
            <div className="flex items-center gap-3">
              {receiptPreview.startsWith('data:image') ? (
                <img src={receiptPreview} alt="Receipt preview" className="w-16 h-16 rounded-lg object-cover border border-slate-200" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-slate-200 flex items-center justify-center"><FileText className="w-6 h-6 text-slate-500" /></div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-slate-900 truncate">{receiptName}</div>
                <div className="text-[11px] text-slate-500">Will be attached on save</div>
              </div>
              <button type="button" onClick={clearReceipt} data-testid="exp-receipt-clear" className="text-slate-500 hover:text-rose-600 p-1.5"><X className="w-4 h-4" /></button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => receiptInputRef.current?.click()}
              data-testid="exp-receipt-upload"
              className="flex items-center gap-2 text-sm font-bold text-slate-700 hover:text-slate-900"
            >
              <Paperclip className="w-4 h-4" /> Attach receipt (optional)
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} data-testid="exp-recurring" /> Recurring</label>
          <button type="submit" disabled={busy} data-testid="exp-submit" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50">{busy ? 'Saving…' : editingId ? 'Update' : 'Add expense'}</button>
        </div>
      </form>

      {/* Filter pills */}
      {expenses.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Show:</span>
          {[
            { id: 'all', label: `All (${expenses.length})` },
            { id: 'with', label: `With receipt (${withReceiptCount})` },
            { id: 'without', label: `Missing receipt (${expenses.length - withReceiptCount})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              data-testid={`exp-filter-${t.id}`}
              className={`text-xs font-bold px-3 py-1.5 rounded-full border transition ${filterReceipt === t.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {loading ? null : filtered.length === 0 ? (
        <Empty text={expenses.length === 0 ? 'No expenses yet — add your first one above.' : 'No expenses match this filter.'} />
      ) : (
        <>
          {/* MOBILE: stacked cards (under md) */}
          <div className="md:hidden space-y-3" data-testid="exp-mobile-list">
            {filtered.map(e => (
              <div key={e.expense_id} className="bg-white rounded-2xl border border-slate-200 p-4 space-y-3" data-testid={`exp-card-${e.expense_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">{(e.date || '').slice(0, 10)}</div>
                    <div className="font-black text-slate-900 truncate mt-0.5">{e.vendor || '—'}</div>
                    <div className="text-sm text-slate-700 mt-0.5 line-clamp-2">{e.description}</div>
                    {e.notes && <div className="text-xs text-slate-500 mt-1 line-clamp-2">{e.notes}</div>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-black text-rose-700 whitespace-nowrap">-{money(e.amount_usd)}</div>
                    <Pill tone="slate">{e.category}{e.recurring ? ' · recurring' : ''}</Pill>
                  </div>
                </div>

                {/* Big tappable receipt action — primary CTA on mobile */}
                {e.has_receipt ? (
                  <button
                    onClick={() => viewReceipt(e)}
                    data-testid={`view-receipt-${e.expense_id}`}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-sm active:scale-[0.98] hover:bg-emerald-100 transition"
                  >
                    <Paperclip className="w-4 h-4" /> View receipt
                  </button>
                ) : (
                  <button
                    onClick={() => attachLater(e.expense_id)}
                    data-testid={`attach-receipt-${e.expense_id}`}
                    className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-slate-50 border border-dashed border-slate-300 text-slate-700 font-bold text-sm active:scale-[0.98] hover:bg-slate-100 transition"
                  >
                    <Upload className="w-4 h-4" /> Add receipt
                  </button>
                )}

                <div className="flex items-center justify-end gap-1 pt-1 border-t border-slate-100">
                  <button onClick={() => edit(e)} data-testid={`edit-exp-${e.expense_id}`} className="text-slate-500 hover:text-blue-600 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1">
                    <Edit2 className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button onClick={() => del(e.expense_id)} data-testid={`del-exp-${e.expense_id}`} className="text-slate-500 hover:text-rose-600 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1">
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* DESKTOP: table (md and up) */}
          <div className="hidden md:block bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-xs uppercase tracking-wider text-slate-600">
                  <Th>Date</Th><Th>Vendor</Th><Th>Description</Th><Th>Category</Th><Th align="center">Receipt</Th><Th align="right">Amount</Th><Th></Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(e => (
                  <tr key={e.expense_id} className="hover:bg-slate-50" data-testid={`exp-row-${e.expense_id}`}>
                    <Td>{(e.date || '').slice(0, 10)}</Td>
                    <Td>{e.vendor || '—'}</Td>
                    <Td bold>{e.description}{e.notes && <div className="text-xs text-slate-500 font-normal">{e.notes}</div>}</Td>
                    <Td><Pill tone="slate">{e.category}{e.recurring ? ' · recurring' : ''}</Pill></Td>
                    <Td align="center">
                      {e.has_receipt ? (
                        <button
                          onClick={() => viewReceipt(e)}
                          data-testid={`view-receipt-desktop-${e.expense_id}`}
                          className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-900 font-bold text-xs px-2 py-1 rounded-full bg-emerald-50 hover:bg-emerald-100 border border-emerald-200"
                        >
                          <Paperclip className="w-3 h-3" /> View
                        </button>
                      ) : (
                        <button
                          onClick={() => attachLater(e.expense_id)}
                          data-testid={`attach-receipt-desktop-${e.expense_id}`}
                          className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800 font-bold text-xs px-2 py-1 rounded-full hover:bg-slate-100"
                        >
                          <Upload className="w-3 h-3" /> Add
                        </button>
                      )}
                    </Td>
                    <Td align="right" bold className="text-rose-700">-{money(e.amount_usd)}</Td>
                    <Td align="right">
                      <button onClick={() => edit(e)} data-testid={`edit-exp-desktop-${e.expense_id}`} className="text-slate-500 hover:text-blue-600 p-1.5"><Edit2 className="w-4 h-4" /></button>
                      <button onClick={() => del(e.expense_id)} data-testid={`del-exp-desktop-${e.expense_id}`} className="text-slate-500 hover:text-rose-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Receipt viewer modal */}
      {viewingReceipt && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setViewingReceipt(null)}
          data-testid="receipt-modal"
        >
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
              <div className="min-w-0">
                <div className="font-black text-slate-900 truncate">{viewingReceipt.vendor || 'Receipt'} · {money(viewingReceipt.amount_usd)}</div>
                <div className="text-xs text-slate-500 truncate">{(viewingReceipt.date || '').slice(0, 10)} · {viewingReceipt.description}</div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <a
                  href={`data:${viewingReceipt.mime_type};base64,${viewingReceipt.image_base64}`}
                  download={viewingReceipt.filename || `receipt-${viewingReceipt.expense_id}.jpg`}
                  data-testid="receipt-download"
                  className="text-slate-500 hover:text-blue-600 p-2"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </a>
                <button
                  onClick={() => removeReceipt(viewingReceipt.expense_id)}
                  data-testid="receipt-remove"
                  className="text-slate-500 hover:text-rose-600 p-2"
                  title="Remove receipt"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button onClick={() => setViewingReceipt(null)} data-testid="receipt-close" className="text-slate-500 hover:text-slate-900 p-2">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-slate-50 p-4 flex items-center justify-center">
              {viewingReceipt.mime_type === 'application/pdf' ? (
                <iframe
                  src={`data:application/pdf;base64,${viewingReceipt.image_base64}`}
                  title="Receipt PDF"
                  className="w-full h-[70vh] rounded-lg bg-white"
                />
              ) : (
                <img
                  src={`data:${viewingReceipt.mime_type};base64,${viewingReceipt.image_base64}`}
                  alt="Receipt"
                  className="max-w-full max-h-[80vh] rounded-lg shadow"
                  data-testid="receipt-image"
                />
              )}
            </div>
          </div>
        </div>
      )}
    </FinanceLayout>
  );
}

// ====================== SETTINGS ======================

export function FinanceSettingsPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [settings, setSettings] = useState(null);
  const [feePct, setFeePct] = useState(2.9);
  const [feeFix, setFeeFix] = useState(0.3);
  const [newCat, setNewCat] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/accounting/settings`, { headers }).then(r => {
      setSettings(r.data); setFeePct(r.data.stripe_fee_percent); setFeeFix(r.data.stripe_fee_fixed);
    });
    // eslint-disable-next-line
  }, []);

  const saveFees = async () => {
    setBusy(true);
    try {
      const r = await axios.put(`${API}/admin/accounting/settings`, { stripe_fee_percent: Number(feePct), stripe_fee_fixed: Number(feeFix) }, { headers });
      setSettings(r.data); toast.success('Fees updated');
    } catch { toast.error('Failed'); }
    finally { setBusy(false); }
  };

  const addCat = async () => {
    const trimmed = newCat.trim();
    if (!trimmed) return;
    const next = [...(settings?.custom_categories || []), trimmed];
    try {
      const r = await axios.put(`${API}/admin/accounting/settings`, { custom_categories: next }, { headers });
      setSettings(r.data); setNewCat(''); toast.success(`Added "${trimmed}"`);
    } catch { toast.error('Failed'); }
  };

  const removeCat = async (c) => {
    const next = (settings?.custom_categories || []).filter(x => x !== c);
    const r = await axios.put(`${API}/admin/accounting/settings`, { custom_categories: next }, { headers });
    setSettings(r.data); toast.success('Removed');
  };

  if (!settings) return <FinanceLayout title="Settings"><div className="py-20" /></FinanceLayout>;

  return (
    <FinanceLayout title="Settings" subtitle="Fees, categories, integrations">
      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-black text-slate-900 flex items-center gap-2"><DollarSign className="w-5 h-5" /> Stripe fee rate</h3>
        <p className="text-sm text-slate-600">Defaults: 2.9% + $0.30. This feeds into the "fees" column everywhere.</p>
        <div className="flex items-end gap-3">
          <L label="Percent"><I type="number" value={feePct} onChange={v => setFeePct(v)} testid="fee-pct" /></L>
          <L label="Fixed (USD)"><I type="number" value={feeFix} onChange={v => setFeeFix(v)} testid="fee-fix" /></L>
          <button onClick={saveFees} disabled={busy} data-testid="save-fees" className="px-5 py-2 rounded-full bg-slate-900 text-white font-bold text-sm disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        </div>
      </section>

      <section className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
        <h3 className="font-black text-slate-900 flex items-center gap-2"><SettingsIcon className="w-5 h-5" /> Custom expense categories</h3>
        <div className="flex flex-wrap gap-2">
          {(settings.categories || []).map(c => {
            const isCustom = (settings.custom_categories || []).includes(c);
            return (
              <span key={c} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold ${isCustom ? 'bg-teal-100 text-teal-800 border border-teal-200' : 'bg-slate-100 text-slate-700'}`}>
                {c}
                {isCustom && <button onClick={() => removeCat(c)} data-testid={`rm-cat-${c}`} className="text-slate-500 hover:text-rose-600">×</button>}
              </span>
            );
          })}
        </div>
        <div className="flex gap-2">
          <input value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="e.g. Advertising, Office" className="flex-1 border border-slate-300 rounded-lg px-3 py-2" data-testid="new-cat" />
          <button onClick={addCat} disabled={!newCat.trim()} data-testid="add-cat" className="px-5 py-2 rounded-full bg-teal-500 text-white font-bold text-sm disabled:opacity-50">Add</button>
        </div>
      </section>

      <section className="bg-gradient-to-r from-blue-50 to-teal-50 border border-blue-200 rounded-2xl p-5">
        <h3 className="font-black text-blue-900 flex items-center gap-2"><SettingsIcon className="w-5 h-5" /> Integrations</h3>
        <p className="text-sm text-slate-700 mt-1">Stripe is already connected. Printful, Shopify, QuickBooks, and Google Sheets connections are on the roadmap. Tell the team what you'd like plugged in first.</p>
      </section>
    </FinanceLayout>
  );
}

// ====================== "COMING SOON" PAGES ======================

export const FinanceInboxPage = () => {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [rawText, setRawText] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [imagePreview, setImagePreview] = useState('');
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [saving, setSaving] = useState(false);
  const [recent, setRecent] = useState([]);
  const [settings, setSettings] = useState(null);
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/admin/accounting/settings`, { headers }),
      axios.get(`${API}/admin/accounting/expenses`, { headers }),
    ]).then(([s, e]) => {
      setSettings(s.data);
      setRecent((e.data || []).slice(0, 5));
    });

    // FAB hand-off: if the user came in via the floating "Add receipt" button,
    // there will be a data-URL waiting in sessionStorage. Pre-fill the preview
    // and immediately auto-parse so they only see the review screen.
    try {
      const dataUrl = sessionStorage.getItem('audiostride_inbox_pending_image');
      if (dataUrl) {
        sessionStorage.removeItem('audiostride_inbox_pending_image');
        sessionStorage.removeItem('audiostride_inbox_pending_filename');
        setImagePreview(dataUrl);
        setImageBase64(dataUrl.split(',')[1] || dataUrl);
      }
    } catch (_) { /* sessionStorage unavailable — fine, regular flow */ }
    // eslint-disable-next-line
  }, []);

  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    for (const it of items) {
      if (it.type && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) handleImage(file);
      }
    }
  };

  const handleImage = (file) => {
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      setImageBase64(reader.result.split(',')[1] || reader.result);
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        handleImage(file);
      } else if (file.type === 'text/plain' || file.type === 'message/rfc822' || file.name.endsWith('.eml')) {
        const reader = new FileReader();
        reader.onload = () => setRawText(reader.result);
        reader.readAsText(file);
      } else {
        toast.error('Drop an image or .txt/.eml file');
      }
    }
  };

  const parse = async () => {
    if (!rawText.trim() && !imageBase64) {
      toast.error('Paste text or drop an image first');
      return;
    }
    setParsing(true);
    try {
      const { data } = await axios.post(`${API}/admin/finance/inbox/parse`, {
        raw_text: rawText, image_base64: imageBase64,
      }, { headers });
      setParsed(data);
    } catch (err) {
      toast.error(errMsg(err, 'Parse failed'));
    } finally { setParsing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        date: parsed.date,
        vendor: parsed.vendor,
        description: parsed.description || parsed.raw_summary,
        amount_usd: Number(parsed.amount_usd),
        category: parsed.category,
        recurring: false,
        notes: parsed.suggested_notes || '',
        // Persist the original receipt so it shows up in the expenses table.
        receipt_image_base64: imageBase64 || undefined,
        receipt_filename: imageBase64 ? `inbox-${Date.now()}.jpg` : undefined,
        receipt_mime_type: imageBase64 ? 'image/jpeg' : undefined,
      };
      const submit = (force) => axios.post(`${API}/admin/accounting/expenses${force ? '?force=true' : ''}`, body, { headers });
      try {
        await submit(false);
      } catch (err) {
        const dedup = parseDedupError(err);
        if (dedup?.kind === 'duplicate_receipt') {
          toast.error(dedup.message || 'This exact receipt is already filed.');
          setSaving(false); return;
        }
        if (dedup?.kind === 'possible_duplicate') {
          if (!window.confirm(`${dedup.message}\n\nFile this expense anyway?`)) {
            setSaving(false); return;
          }
          await submit(true);
        } else {
          throw err;
        }
      }
      toast.success(`Saved ${money(body.amount_usd)} expense for ${body.vendor}${imageBase64 ? ' (receipt attached)' : ''}`);
      setRawText(''); setImageBase64(''); setImagePreview(''); setParsed(null);
      const e = await axios.get(`${API}/admin/accounting/expenses`, { headers });
      setRecent((e.data || []).slice(0, 5));
    } catch (err) {
      toast.error(errMsg(err, 'Save failed'));
    } finally { setSaving(false); }
  };

  const reset = () => { setRawText(''); setImageBase64(''); setImagePreview(''); setParsed(null); };

  return (
    <FinanceLayout title="Finance Inbox" subtitle="AI-parse receipts, invoices, and supplier emails into expenses">
      <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Inbox className="w-5 h-5 text-teal-700 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="font-bold text-teal-900">Forward → Paste → Save. About 5 seconds per receipt.</p>
            <p className="text-teal-800 mt-1">In Gmail, open a receipt (Resend, Stripe, anything), select all (Ctrl/Cmd+A), copy, paste below. Or just drag a screenshot/PDF onto the page. Claude will extract vendor, amount, date and category — review and save.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* INPUT */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4" onPaste={onPaste} onDrop={onDrop} onDragOver={(e) => e.preventDefault()}>
          <h3 className="font-black text-slate-900 text-base sm:text-lg">1. Upload your receipt</h3>

          {imagePreview ? (
            <div className="relative">
              <img src={imagePreview} alt="Receipt preview" className="max-h-72 w-full rounded-lg border border-slate-200 object-contain bg-slate-50" />
              <button onClick={() => { setImagePreview(''); setImageBase64(''); }} data-testid="clear-image" className="absolute top-2 right-2 bg-white border border-slate-200 rounded-full p-1.5 shadow hover:bg-slate-50">
                <X className="w-4 h-4 text-slate-600" />
              </button>
            </div>
          ) : (
            <>
              {/* Big mobile-friendly capture buttons */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) handleImage(e.target.files[0]); e.target.value = ''; }}
                data-testid="camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.eml,.txt"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.type.startsWith('image/')) handleImage(f);
                  else { const r = new FileReader(); r.onload = () => setRawText(r.result); r.readAsText(f); }
                  e.target.value = '';
                }}
                data-testid="file-input"
              />

              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => cameraInputRef.current?.click()}
                  data-testid="take-photo-btn"
                  className="flex flex-col items-center justify-center gap-2 py-6 sm:py-8 rounded-2xl bg-gradient-to-br from-teal-500 to-blue-600 text-white font-bold shadow-lg active:scale-95 hover:opacity-95 transition"
                >
                  <Camera className="w-7 h-7 sm:w-8 sm:h-8" />
                  <span className="text-sm sm:text-base">Take photo</span>
                </button>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-photo-btn"
                  className="flex flex-col items-center justify-center gap-2 py-6 sm:py-8 rounded-2xl bg-slate-900 text-white font-bold shadow-lg active:scale-95 hover:bg-black transition"
                >
                  <ImageIcon className="w-7 h-7 sm:w-8 sm:h-8" />
                  <span className="text-sm sm:text-base">From library</span>
                </button>
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                data-testid="upload-zone-btn"
                className="hidden sm:flex w-full flex-col items-center justify-center gap-1 py-5 rounded-2xl border-2 border-dashed border-teal-300 bg-gradient-to-br from-teal-50 to-blue-50 hover:from-teal-100 hover:to-blue-100 transition cursor-pointer text-center"
              >
                <Upload className="w-7 h-7 text-teal-600" />
                <span className="font-bold text-slate-900 text-sm">Or drop a screenshot / paste anywhere on the page</span>
                <span className="text-xs text-slate-500">PDF, image, or .eml</span>
              </button>
            </>
          )}

          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-white px-3 text-slate-500 font-bold uppercase tracking-wider">or paste email text</span></div>
          </div>

          <textarea
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            placeholder="Paste the entire email here (subject + body)..."
            rows={6}
            className="w-full border border-slate-300 rounded-lg p-3 text-sm font-mono"
            data-testid="raw-text-input"
          />

          <div className="flex justify-between items-center gap-2">
            <button onClick={reset} className="text-sm text-slate-500 hover:text-slate-700 px-2 py-2">Clear</button>
            <button
              onClick={parse}
              disabled={parsing || (!rawText.trim() && !imageBase64)}
              data-testid="parse-btn"
              className="flex-1 sm:flex-none px-5 py-3 sm:py-2.5 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold disabled:opacity-50 flex items-center justify-center gap-2 text-base sm:text-sm shadow-lg active:scale-95 transition"
            >
              <Sparkles className="w-5 h-5 sm:w-4 sm:h-4" />
              {parsing ? 'Reading…' : 'Parse with AI'}
            </button>
          </div>
        </div>

        {/* RESULT */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-4">
          <h3 className="font-black text-slate-900">2. Review &amp; save</h3>
          {!parsed ? (
            <div className="border-2 border-dashed border-slate-200 rounded-xl py-16 text-center text-slate-400 text-sm">
              Parsed expense will appear here
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Pill tone={parsed.confidence === 'high' ? 'emerald' : parsed.confidence === 'medium' ? 'amber' : 'rose'}>
                  {parsed.confidence.toUpperCase()} CONFIDENCE
                </Pill>
                <span className="text-xs text-slate-500">via {parsed.model_used}</span>
              </div>
              {parsed.raw_summary && <p className="text-sm text-slate-600 italic">"{parsed.raw_summary}"</p>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <L label="Vendor"><I value={parsed.vendor} onChange={v => setParsed({ ...parsed, vendor: v })} testid="parsed-vendor" /></L>
                <L label="Amount (USD)"><I type="number" value={parsed.amount_usd} onChange={v => setParsed({ ...parsed, amount_usd: v })} testid="parsed-amount" /></L>
                <L label="Date"><I type="date" value={parsed.date} onChange={v => setParsed({ ...parsed, date: v })} testid="parsed-date" /></L>
                <L label="Category">
                  <select value={parsed.category} onChange={e => setParsed({ ...parsed, category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 bg-white" data-testid="parsed-category">
                    {(settings?.categories || []).map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </L>
                <L label="Description" spans={2}><I value={parsed.description} onChange={v => setParsed({ ...parsed, description: v })} testid="parsed-desc" /></L>
                <L label="Notes" spans={2}><I value={parsed.suggested_notes} onChange={v => setParsed({ ...parsed, suggested_notes: v })} testid="parsed-notes" /></L>
              </div>

              <div className="flex justify-end gap-2">
                <button onClick={() => setParsed(null)} className="px-4 py-2 rounded-full bg-slate-100 font-bold text-sm">Discard</button>
                <button onClick={save} disabled={saving} data-testid="save-expense-btn" className="px-5 py-2 rounded-full bg-gradient-to-r from-teal-500 to-blue-600 text-white font-bold text-sm disabled:opacity-50 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  {saving ? 'Saving…' : 'Save as Audiostride expense'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {recent.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-5">
          <h3 className="font-black text-slate-900 mb-3 text-sm uppercase tracking-wider">Recently filed</h3>
          <ul className="divide-y divide-slate-100 text-sm">
            {recent.map(e => (
              <li key={e.expense_id} className="flex items-center justify-between py-2">
                <div>
                  <div className="font-bold text-slate-900">{e.description}</div>
                  <div className="text-xs text-slate-500">{e.vendor || '—'} · {e.category} · {(e.date || '').slice(0,10)}</div>
                </div>
                <div className="font-bold text-rose-700">-{money(e.amount_usd)}</div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm">
        <p className="font-bold text-amber-900">📬 Want true forward-and-forget automation?</p>
        <p className="text-amber-800 mt-1">Setting up a real <code className="bg-white px-1 py-0.5 rounded">finance@audiostride.com</code> address that auto-parses without copy-paste requires either dedicated MX records (would conflict with your Porkbun email forwarding for <code className="bg-white px-1 py-0.5 rounded">hello@</code> and <code className="bg-white px-1 py-0.5 rounded">contact@</code>) or a paid inbound-email service like Mailgun/CloudMailin (~$15/mo + 1 day to configure). Most founders find this paste flow takes 5 seconds per receipt and lets you <em>review</em> before committing — recommended for the first ~50 receipts. Tell me when you'd like the full automation.</p>
      </div>
    </FinanceLayout>
  );
};

export const FinanceContractsPage = () => (
  <FinanceLayout title="Contracts">
    <ComingSoon title="Contracts" icon={FileSignature}
      what="Upload advertiser agreements, sponsorship contracts, and analytics subscription addenda. Link each contract to its advertiser/client/campaign, track expiration dates, and get renewal reminders."
      needs={<>Decide storage: <strong>Built-in cloud storage</strong> (already set up, free with the platform) or <strong>Google Drive</strong> (requires Google Workspace integration). Most solo founders choose the built-in storage — reply "use built-in storage" and I'll build it in one session.</>} />
  </FinanceLayout>
);

export const FinanceTaxPage = () => (
  <FinanceLayout title="Tax / VAT">
    <ComingSoon title="Tax / VAT Center" icon={Landmark}
      what="Country-based tax rules (US sales tax, EU VAT, Israel VAT, UK VAT), automatic VAT charging on invoices based on client country, VAT-recoverable tracking on expenses, and accountant-ready VAT return exports."
      needs={<>Tell me: <strong>(1)</strong> where Audiostride is incorporated / pays tax, <strong>(2)</strong> whether you're VAT-registered (Israel VAT? EU OSS?), <strong>(3)</strong> the default VAT rate you charge customers, and <strong>(4)</strong> any specific accountant-report formats your accountant needs. Once I have those I can build the whole tax engine in one go.</>} />
  </FinanceLayout>
);

export const FinanceForecastPage = () => (
  <FinanceLayout title="Forecasting">
    <ComingSoon title="Forecasting" icon={Sparkles}
      what="AI-driven projections for the next 90 days — subscription renewals, ad campaign renewals, merch seasonality (spring break, holiday spike), churn risk, and inventory needs. Gets smarter as data accumulates."
      needs={<>Forecasts need <strong>at least 3 months</strong> of real revenue data to be meaningful — otherwise they're just guessing. The foundation is already in place; we'll activate forecasting once you've had ~3 months of live subscriptions. No action needed from you now — just keep shipping.</>} />
  </FinanceLayout>
);

// ====================== shared micro-components ======================
const L = ({ label, req, children, spans }) => (
  <label className={`block ${spans === 2 ? 'md:col-span-2' : spans === 3 ? 'md:col-span-3' : ''}`}>
    <span className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">{label}{req && <span className="text-rose-500"> *</span>}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const I = ({ value, onChange, type = 'text', testid, placeholder }) => (
  <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid={testid} />
);
const Th = ({ children, align = 'left' }) => <th className={`px-4 py-3 text-${align} font-bold`}>{children}</th>;
const Td = ({ children, align = 'left', bold, className = '' }) => <td className={`px-4 py-2.5 text-${align} ${bold ? 'font-bold text-slate-900' : ''} ${className}`}>{children}</td>;
